import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  HomeIcon,
  DocumentTextIcon,
  NewspaperIcon,
  UserGroupIcon,
  UserIcon,
  ChatBubbleLeftRightIcon,
  PresentationChartLineIcon,
  CommandLineIcon,
  XMarkIcon,
  BookOpenIcon,
  LightBulbIcon,
  QueueListIcon,
  CpuChipIcon,
  EnvelopeIcon,
  PaperAirplaneIcon,
  ChartBarIcon,
  BoltIcon,
} from '@heroicons/react/24/outline';

const navigationItems = [
  { path: '/dashboard', label: 'Dashboard', icon: HomeIcon },
  { path: '/issues', label: 'Issues', icon: NewspaperIcon },
  { path: '/items', label: 'Items', icon: DocumentTextIcon },
  { path: '/authors', label: 'Authors', icon: UserGroupIcon },
  { path: '/ask-obn', label: 'Ask OBN', icon: ChatBubbleLeftRightIcon },
  { path: '/live-dashboard', label: 'Live Dashboard', icon: PresentationChartLineIcon },
  { path: '/commands', label: 'Commands', icon: CommandLineIcon },
  {
    label: 'Email',
    icon: EnvelopeIcon,
    children: [
      { path: '/email/single-sends', label: 'Single Sends', icon: PaperAirplaneIcon },
      { path: '/email/contacts', label: 'Contacts', icon: UserGroupIcon },
      { path: '/email/contact-stats', label: 'Contact Stats', icon: ChartBarIcon },
      { path: '/email/events', label: 'Events', icon: BoltIcon },
    ],
  },
  {
    label: 'AI Config',
    icon: CpuChipIcon,
    children: [
      { path: '/style-guide', label: 'Style Guide', icon: BookOpenIcon },
      { path: '/opinions', label: 'Opinions', icon: LightBulbIcon },
      { path: '/section-guides', label: 'Section Guides', icon: QueueListIcon },
    ],
  },
  { path: '/profile', label: 'Profile', icon: UserIcon },
];

function Sidebar({ isCollapsed, setIsCollapsed, isMobileOpen, setIsMobileOpen }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [expandedSections, setExpandedSections] = useState(() => {
    // Auto-expand sections that contain the current route
    const expanded = {};
    navigationItems.forEach((item) => {
      if (item.children) {
        const isChildActive = item.children.some(
          (child) => location.pathname === child.path || location.pathname.startsWith(child.path)
        );
        if (isChildActive) {
          expanded[item.label] = true;
        }
      }
    });
    return expanded;
  });

  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
  };

  const closeMobileSidebar = () => {
    setIsMobileOpen(false);
  };

  const toggleSection = (label) => {
    setExpandedSections((prev) => ({
      ...prev,
      [label]: !prev[label],
    }));
  };

  const isExpanded = (!isCollapsed || isMobileOpen);

  const renderNavItem = (item, isChild = false) => {
    const IconComponent = item.icon;
    const isActive =
      location.pathname === item.path ||
      (item.path !== '/dashboard' && location.pathname.startsWith(item.path));

    const handleClick = () => {
      closeMobileSidebar();
      // Always navigate, even if we're on a sub-route of this path
      navigate(item.path);
    };

    return (
      <button
        key={item.path}
        onClick={handleClick}
        className={`
          w-full group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200
          ${isChild && isExpanded ? 'ml-4' : ''}
          ${isActive
            ? 'bg-white/20 text-white border-r-2 border-white'
            : 'text-white/80 hover:bg-white/10 hover:text-white'
          }
        `}
      >
        <IconComponent
          className={`
            flex-shrink-0 h-5 w-5 transition-colors duration-200
            ${isActive ? 'text-white' : 'text-white/60 group-hover:text-white/80'}
          `}
        />
        {isExpanded && <span className="ml-3 truncate">{item.label}</span>}

        {/* Active indicator for collapsed state */}
        {!isExpanded && isActive && (
          <div className="absolute left-0 w-1 h-8 bg-white rounded-r-full"></div>
        )}
      </button>
    );
  };

  const renderNavSection = (item) => {
    const IconComponent = item.icon;
    const isSectionExpanded = expandedSections[item.label];
    const hasActiveChild = item.children.some(
      (child) => location.pathname === child.path || location.pathname.startsWith(child.path)
    );

    return (
      <div key={item.label}>
        <button
          onClick={() => toggleSection(item.label)}
          className={`
            w-full group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200
            ${hasActiveChild
              ? 'bg-white/10 text-white'
              : 'text-white/80 hover:bg-white/10 hover:text-white'
            }
          `}
        >
          <IconComponent
            className={`
              flex-shrink-0 h-5 w-5 transition-colors duration-200
              ${hasActiveChild ? 'text-white' : 'text-white/60 group-hover:text-white/80'}
            `}
          />
          {isExpanded && (
            <>
              <span className="ml-3 truncate flex-1 text-left">{item.label}</span>
              <ChevronDownIcon
                className={`
                  h-4 w-4 text-white/60 transition-transform duration-200
                  ${isSectionExpanded ? 'rotate-180' : ''}
                `}
              />
            </>
          )}
        </button>

        {/* Children - show when expanded or in collapsed mode show on hover/active */}
        {isExpanded && isSectionExpanded && (
          <div className="mt-1 space-y-1">
            {item.children.map((child) => renderNavItem(child, true))}
          </div>
        )}

        {/* In collapsed mode, show children in a tooltip/popup on hover - simplified: just show active indicator */}
        {!isExpanded && hasActiveChild && (
          <div className="absolute left-0 w-1 h-8 bg-white rounded-r-full"></div>
        )}
      </div>
    );
  };

  const SidebarContent = () => (
    <div className="flex h-full flex-col">
      {/* Logo/Header */}
      <div className="flex h-16 shrink-0 items-center justify-between px-4 border-b border-white/20">
        <div className="flex items-center">
          {!isExpanded ? (
            // Collapsed state - show just the "O" character
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center border border-white/30">
                <span className="text-white font-bold text-sm">O</span>
              </div>
            </div>
          ) : (
            // Expanded state - show full logo
            <div className="flex items-center">
              <img
                src="/off-by-none-logo.svg"
                alt="Off-by-none"
                className="h-8 w-auto max-w-none"
              />
            </div>
          )}
        </div>

        {/* Desktop collapse toggle */}
        <button
          onClick={toggleSidebar}
          className="hidden lg:flex p-1.5 rounded-lg hover:bg-white/10 transition-colors"
        >
          {isCollapsed ? (
            <ChevronRightIcon className="h-4 w-4 text-white/70" />
          ) : (
            <ChevronLeftIcon className="h-4 w-4 text-white/70" />
          )}
        </button>

        {/* Mobile close button */}
        <button
          onClick={closeMobileSidebar}
          className="lg:hidden p-1.5 rounded-lg hover:bg-white/10 transition-colors"
        >
          <XMarkIcon className="h-5 w-5 text-white/70" />
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-4 space-y-1">
        {navigationItems.map((item) =>
          item.children ? renderNavSection(item) : renderNavItem(item)
        )}
      </nav>
    </div>
  );

  return (
    <>
      {/* Mobile backdrop */}
      {isMobileOpen && (
        <div 
          className="fixed inset-0 z-40 bg-black bg-opacity-25 lg:hidden"
          onClick={closeMobileSidebar}
        />
      )}
      
      {/* Desktop sidebar */}
      <div className={`
        hidden lg:flex lg:flex-col lg:fixed lg:inset-y-0 lg:z-50 lg:border-r lg:border-white/10
        transition-all duration-300 ease-in-out
        ${isCollapsed ? 'lg:w-16' : 'lg:w-64'}
      `}>
        <SidebarContent />
      </div>
      
      {/* Mobile sidebar */}
      <div className={`
        fixed inset-y-0 left-0 z-50 w-64 border-r border-white/10 shadow-lg transform transition-transform duration-300 ease-in-out lg:hidden
        ${isMobileOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <SidebarContent />
      </div>
    </>
  );
}

export default Sidebar;
